import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CalendarClock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import NativeSelect from "@/components/forms/NativeSelect";
import PhoneVerifyInline from "@/components/auth/PhoneVerifyInline";
import api from "@/lib/api";

/** Today's date in YYYY-MM-DD (local time, suitable for an HTML date input min). */
function todayIso() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Small row inside the "Before you confirm" checklist. Green tick when done,
 *  neutral circle when pending — kept lightweight to fit inside the dialog. */
function ChecklistItem({ ok, label, testId }) {
  return (
    <div className="flex items-center gap-2" data-testid={testId} data-state={ok ? "done" : "pending"}>
      <span
        aria-hidden="true"
        className={`inline-flex items-center justify-center h-4 w-4 rounded-full border text-[10px] ${
          ok
            ? "bg-[#1F4E3D] text-white border-[#1F4E3D]"
            : "bg-white text-[#7A5A22] border-[#E7DDCF]"
        }`}
      >
        {ok ? "✓" : ""}
      </span>
      <span className={ok ? "text-[#1F4E3D]" : "text-[#4B5563]"}>{label}</span>
    </div>
  );
}

/**
 * <BookingDialog /> — Premium-only "Book Appointment" flow.
 *
 * Step 1: pick a date         → fetches /api/spas/{slug}/booking-slots
 * Step 2: pick a time slot    → enabled once slots are loaded and at least one is free
 * Step 3: fill contact + service + notes
 * Submit → POST /api/spas/{slug}/bookings
 *
 * Props:
 *   slug              string                       - spa slug
 *   services          string[]                     - list of services from the spa (optional)
 *   slotMinutes       number                       - declared slot length, used in the helper text
 *   triggerClassName  string                       - extra className applied to the trigger button
 *   colorScheme       "green" | "brown"            - drives the trigger button palette so the
 *                                                    parent can place it in an alternating-color
 *                                                    CTA stack. Defaults to "brown" (gold).
 */
export default function BookingDialog({ slug, services = [], slotMinutes = 60, triggerClassName = "", colorScheme = "brown" }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(todayIso());
  const [slotsData, setSlotsData] = useState(null);  // {slots, all_slots, closed, weekday}
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    service_name: "",
    time_slot: "",
    notes: "",
    website: "",  // honeypot — keep empty
  });
  const [submitting, setSubmitting] = useState(false);
  // Phone-OTP verification: token issued by POST /auth/phone/verify?mode=booking.
  // Bookings can NOT be submitted until this is truthy. OTP is now the ONLY
  // anti-bot check for bookings (SMS delivery to a real handset is a stronger
  // signal than hCaptcha and gives a smoother UX inside the modal).
  const [phoneVerificationToken, setPhoneVerificationToken] = useState(null);

  const reset = useCallback(() => {
    setDate(todayIso());
    setSlotsData(null);
    setForm({
      customer_name: "",
      customer_phone: "",
      customer_email: "",
      service_name: "",
      time_slot: "",
      notes: "",
      website: "",
    });
    setPhoneVerificationToken(null);
  }, []);

  // Reset everything whenever the dialog re-opens so the user gets a clean form.
  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const loadSlots = useCallback(async (forDate) => {
    setLoadingSlots(true);
    try {
      const r = await api.get(`/spas/${slug}/booking-slots`, { params: { date: forDate } });
      setSlotsData(r.data);
      setForm((f) => ({ ...f, time_slot: "" }));
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not load available slots";
      toast.error(msg);
      setSlotsData({ slots: [], all_slots: [], closed: false, weekday: "" });
    } finally {
      setLoadingSlots(false);
    }
  }, [slug]);

  // Auto-load slots when the dialog opens or the date changes.
  useEffect(() => {
    if (open && date) loadSlots(date);
  }, [open, date, loadSlots]);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_phone || !form.time_slot) {
      toast.error("Name, phone and time slot are required");
      return;
    }
    if (!phoneVerificationToken) {
      toast.error("Please verify your phone via OTP before confirming the booking.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/spas/${slug}/bookings`, {
        ...form,
        date,
        phone_verification_token: phoneVerificationToken,
      });
      toast.success("Booking request sent! The spa will confirm shortly.");
      setOpen(false);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to create booking";
      toast.error(msg);
      // A 401 means the phone_verification_token expired mid-flight — force re-verify.
      if (err.response?.status === 401) {
        setPhoneVerificationToken(null);
      }
      // If 409, refresh the slots so the just-taken slot disappears.
      if (err.response?.status === 409) loadSlots(date);
    } finally {
      setSubmitting(false);
    }
  }, [slug, form, date, phoneVerificationToken, loadSlots]);

  const minDate = useMemo(() => todayIso(), []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          className={`w-full h-11 gap-2 ${
            colorScheme === "green"
              ? "bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white"
              : "bg-[#C9A86A] hover:bg-[#D7BC86] text-[#111827]"
          } ${triggerClassName}`}
          data-testid="spa-booking-open-button"
        >
          <CalendarClock size={16} /> Book Appointment
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Book an appointment</DialogTitle>
          <DialogDescription>
            Select a date and a {slotMinutes}-minute time slot. The spa will confirm by phone.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3" data-testid="booking-form">
          {/* Date */}
          <div>
            <Label className="text-xs">Date *</Label>
            <Input
              type="date"
              value={date}
              min={minDate}
              onChange={(e) => setDate(e.target.value)}
              required
              data-testid="booking-date-input"
            />
          </div>

          {/* Slot picker */}
          <div>
            <Label className="text-xs">Time slot *</Label>
            {loadingSlots ? (
              <div className="flex items-center gap-2 text-sm text-[#4B5563] py-2" data-testid="booking-slots-loading">
                <Loader2 size={14} className="animate-spin" /> Loading slots…
              </div>
            ) : slotsData?.closed ? (
              <div className="text-sm text-[#7A5A22] py-2" data-testid="booking-slots-closed">
                The spa is closed on this day. Please pick another date.
              </div>
            ) : (slotsData?.slots?.length ?? 0) === 0 ? (
              <div className="text-sm text-[#7A5A22] py-2" data-testid="booking-slots-empty">
                No slots available on this date. Try another day.
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2" data-testid="booking-slots-grid">
                {slotsData.slots.map((s) => {
                  const selected = form.time_slot === s;
                  return (
                    <button
                      type="button"
                      key={s}
                      onClick={() => setForm({ ...form, time_slot: s })}
                      className={`text-xs h-8 rounded-md border transition-colors ${
                        selected
                          ? "bg-[#1F4E3D] text-white border-[#1F4E3D]"
                          : "bg-white border-[#E7DDCF] hover:border-[#C9A86A] hover:bg-[#F8F1E4] text-[#111827]"
                      }`}
                      data-testid={`booking-slot-${s.replace(":", "")}`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Service (optional) */}
          {services.length > 0 && (
            <div>
              <Label className="text-xs">Service (optional)</Label>
              <NativeSelect
                value={form.service_name}
                onChange={(v) => setForm({ ...form, service_name: v })}
                data-testid="booking-service-select"
              >
                <option value="">Any service</option>
                {services.map((s) => <option key={s} value={s}>{s}</option>)}
              </NativeSelect>
            </div>
          )}

          {/* Contact — Name & Phone (Phone requires OTP verification) */}
          <div>
            <Label className="text-xs">Your name *</Label>
            <Input
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              required
              data-testid="booking-name-input"
            />
          </div>

          <PhoneVerifyInline
            phone={form.customer_phone}
            onPhoneChange={(p) => setForm((f) => ({ ...f, customer_phone: p }))}
            verificationToken={phoneVerificationToken}
            onVerified={({ phone, verification_token }) => {
              // Keep the local form state in sync with the E.164 number the
              // backend verified — the last 10 digits must match on submit.
              const digits = String(phone || "").replace(/\D/g, "").slice(-10);
              setForm((f) => ({ ...f, customer_phone: digits || f.customer_phone }));
              setPhoneVerificationToken(verification_token);
            }}
            onReset={() => setPhoneVerificationToken(null)}
            idSuffix={slug}
          />

          <div>
            <Label className="text-xs">Email (optional)</Label>
            <Input
              type="email"
              value={form.customer_email}
              onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
              data-testid="booking-email-input"
            />
          </div>

          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="booking-notes-input"
            />
          </div>

          {/* Honeypot — hidden from real users; bots auto-fill it. */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", overflow: "hidden" }}>
            <label>
              Website (leave blank)
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                data-testid="booking-honeypot-input"
              />
            </label>
          </div>

          {/*
            Confirm-booking readiness checklist. Both items must be green
            before submit will succeed — otherwise the submit handler shows a
            targeted toast explaining what's still missing.
          */}
          <div
            className="text-[11px] rounded-md border border-[#E7DDCF] bg-[#FBF7F1] p-2 space-y-1"
            data-testid="booking-checklist"
          >
            <div className="font-medium text-[#7A5A22] uppercase tracking-widest text-[10px]">Before you confirm</div>
            <ChecklistItem ok={!!form.time_slot} label="Time slot selected" testId="chk-slot" />
            <ChecklistItem ok={!!phoneVerificationToken} label="Phone verified via OTP" testId="chk-phone" />
          </div>

          <DialogFooter>
            {/*
              Keep the button ALWAYS clickable. The submit handler validates and
              surfaces a specific toast for whichever piece is missing. This
              avoids the confusing "greyed-out button with no explanation" state
              — especially when hCaptcha's UI shows a checked box but its
              onVerify callback silently failed to fire due to Radix Dialog's
              focus interference.
            */}
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="booking-submit-button"
            >
              {submitting ? "Booking…" : "Confirm booking"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
